
import { newsletterCategories } from '~/src/modules/customer/types/newsletterCategories';
import NewsletterModal from '@root/modules/customer/components/newsletter/NewsletterModal.vue';
import { faCalendar, faCircleInfo, faPlus, faXmark } from '@delfi.lt/fa-kit/icons/modules/classic/regular';

interface NewsletterlWindow extends Window {
  tp: any;
}

declare const window: NewsletterlWindow;

import Vue from 'vue';

export default Vue.extend({
  components: {
    NewsletterModal,
  },
  props: {
    customerToken: {
      type: String,
    },
    customerHasAccess: {
      type: Boolean,
    },
    activePage: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      displayName: '',
      agreementChecked: false,
      agreementConfirmed: false,
      isEmailVerified: false,
      showEmailVerificationModal: false,
      showAgreementError: false,
      showNoNewsletterSelectedModal: false,
      showUnsubscribeConfirmModal: false,
      initialAgreement: false,
      categories: JSON.parse(JSON.stringify(newsletterCategories)),
      showNoChangesModal: false,
      initialSubscribedNames: [] as string[],
      showSuccessModal: false,
      showUnsavedChangesModal: false,
      faCalendar,
      faCircleInfo,
      faPlus,
      faXmark,
    };
  },
  watch: {
    agreementChecked(val: boolean) {
      if (val) {
        this.showAgreementError = false;
      }
    },
  },
  computed: {
    stickyEnabled(): boolean {
      return this.hasChanges();
    },
  },
  methods: {
    async fetchSubscribedItems() {
      try {
        for (const category of this.categories) {
          for (const item of category.items) {
            item.loading = true;
          }
        }
        /** pirma perašom iš anoniminių į customfieldą */
        await fetch('https://paywall.delfi.lt/newsletter/move-anonymouslits-to-reglist', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.customerToken}`,
          },
        });
        /** imam iš customfieldo */
        const response = await fetch('https://paywall.delfi.lt/newsletter/get-reg-esp-list', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.customerToken}`,
          },
        });

        const data = await response.json();
        if (data && Array.isArray(data.inList)) {
          const subscribedNames = data.inList.map((entry: { inList: string }) => entry.inList);
          this.markSubscribed(subscribedNames);
          this.initialSubscribedNames = [...subscribedNames];
        }
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      } finally {
        for (const category of this.categories) {
          for (const item of category.items) {
            item.loading = false;
          }
        }
      }
    },
    markSubscribed(subscribedNames: string[]) {
      for (const category of this.categories) {
        for (const item of category.items) {
          item.subscribed = subscribedNames.includes(item.name);
        }
      }
    },
    async toggleSubscription(type: string, catIndex: number, index: number) {
      const item = this.categories[catIndex].items[index];
      if (!item || item.type !== type || item.loading) return;

      item.loading = true;
      item.subscribed = !item.subscribed;
      item.loading = false;
    },
    async updateSubscription(espId: number, action: 'subscribe' | 'unsubscribe'): Promise<boolean> {
      const endpoint = `https://paywall.delfi.lt/newsletter/update-newsletter-list`;
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.customerToken}`,
          },
          body: JSON.stringify({
            espId,
            action,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to ${action}`);
        }

        return true;
      } catch (error) {
        console.error(`${action} error:`, error);
        return false;
      }
    },
    async saveSubscription() {
      if (this.activePage === 'FirstPage' && this.initialAgreement && !this.agreementChecked) {
        this.showUnsubscribeConfirmModal = true;
        return;
      }

      if (!this.validateAgreement()) return;

      if (!this.hasChanges()) {
        this.showNoChangesModal = true;
        return;
      }
      const selectedNewsletterNames = this.collectSelectedNewsletterNames();

      if (selectedNewsletterNames.length === 0 && this.activePage !== 'FirstPage') {
        this.showNoNewsletterSelectedModal = true;
        return;
      }

      const customFields = this.buildCustomFields(selectedNewsletterNames);

      const success = await this.updatePianoCustomFields(customFields);

      if (success) {
        await this.handlePostAgreement();
        this.showSuccessModal = true;
        this.initialSubscribedNames = this.collectSelectedNewsletterNames();
        this.initialAgreement = this.agreementChecked;
      }
    },
    validateAgreement(): boolean {
      if (!this.agreementChecked) {
        this.showAgreementError = true;
        return false;
      }

      this.showAgreementError = false;
      return true;
    },
    collectSelectedNewsletterNames(): string[] {
      const names: string[] = [];

      for (const category of this.categories) {
        for (const item of category.items) {
          if (item.subscribed) names.push(item.name);
        }
      }

      return names;
    },
    buildCustomFields(newsletterNames: string[]) {
      const fields = [
        {
          field_name: 'newsletter-agreement',
          value: 'true',
        },
        {
          field_name: 'nl-agreement-change',
          value: 'true',
        },
        {
          field_name: 'newsletter-esp-list',
          value: JSON.stringify(newsletterNames),
        },
        {
          field_name: 'newsletter-esp-updated',
          value: new Date().toISOString().split('T')[0],
        },
      ];

      if (this.isEmailVerified) {
        fields.push({
          field_name: 'newsletter-export',
          value: 'true',
        });
      }

      return fields;
    },
    hasAnySelected(): boolean {
      for (const category of this.categories) {
        for (const item of category.items) {
          if (item.subscribed) return true;
        }
      }
      return false;
    },
    async updatePianoCustomFields(customFields: any[]): Promise<boolean> {
      try {
        const token = window.tp.pianoId.getToken();
        const uid = window.tp.pianoId.getUser()?.uid;

        if (!token || !uid) throw new Error('Missing Piano token or UID');

        const response = await fetch(`https://api-eu.piano.io/id/api/v1/identity/userinfo?lang=en_US&aid=${window.tp.aid}&access_token=${token}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json; charset=UTF-8' },
          body: JSON.stringify({ uid, custom_field_values: customFields }),
        });

        if (!response.ok) {
          const errorDetails = await response.json();
          console.error('API error details:', errorDetails);
          throw new Error('Failed to update custom fields');
        }
        console.log('Custom fields updated:', await response.json());
        return true;
      } catch (error) {
        console.error('Error updating user custom fields:', error);
        return false;
      }
    },
    async handlePostAgreement() {
      if (this.hasAnySelected()) {
        if (!this.isEmailVerified) {
          this.showEmailVerificationModal = true;
          await this.updateSubscription(10476, 'subscribe');
        } else {
          this.agreementConfirmed = true;
        }
      } else {
        this.agreementConfirmed = true;
      }
    },
    closeEmailVerificationModal() {
      this.showEmailVerificationModal = false;
      if (this.activePage !== 'FirstPage') {
        window.location.href = 'https://www.delfi.lt/mano-delfi';
      }
    },
    skipSubscription() {
      window.location.href = 'https://www.delfi.lt/mano-delfi';
    },
    async confirmUnsubscribeAll() {
      const customFields = [
        {
          field_name: 'newsletter-agreement',
          value: 'false',
        },
        {
          field_name: 'nl-agreement-change',
          value: 'true',
        },
        {
          field_name: 'newsletter-esp-list',
          value: JSON.stringify([]),
        },
        {
          field_name: 'newsletter-esp-updated',
          value: new Date().toISOString().split('T')[0],
        },
        {
          field_name: 'newsletter-export',
          value: 'true',
        },
      ];

      const success = await this.updatePianoCustomFields(customFields);

      if (success) {
        for (const category of this.categories) {
          for (const item of category.items) {
            item.subscribed = false;
          }
        }

        this.agreementChecked = false;
        this.initialAgreement = false;
        this.showUnsubscribeConfirmModal = false;
      }
    },
    hasChanges(): boolean {
      const current = this.collectSelectedNewsletterNames();

      if (current.length !== this.initialSubscribedNames.length) {
        return true;
      }

      const initialSet = new Set(this.initialSubscribedNames);
      const currentSet = new Set(current);

      if (initialSet.size !== currentSet.size) return true;

      for (const name of currentSet) {
        if (!initialSet.has(name)) return true;
      }

      return false;
    },
    cancelUnsubscribeAll() {
      this.agreementChecked = true;
      this.showUnsubscribeConfirmModal = false;
    },
    handleClickOutside(event: MouseEvent) {
      const wrapper = this.$el as HTMLElement;
      if (wrapper && !wrapper.contains(event.target as Node)) {
        if (this.hasChanges()) {
          const target = event.target as HTMLElement;
          const link = target.closest('a');
          if (link) {
            event.preventDefault();
            event.stopPropagation();
            this.showUnsavedChangesModal = true;
          }
        }
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    const initPiano = () => {
      if (window.tp && typeof window.tp.push === 'function') {
        window.tp.push([
          'init',
          () => {
            if (window.tp?.pianoId?.loadExtendedUser) {
              window.tp.pianoId.loadExtendedUser({
                extendedUserLoaded: (data: any) => {
                  if (data?.custom_field_values) {
                    const agreementField = data.custom_field_values.find((f: any) => f.field_name === 'newsletter-agreement');
                    const espListField = data.custom_field_values.find((f: any) => f.field_name === 'newsletter-esp-list');
                    if (espListField && espListField.value) {
                      let espNames: string[] = [];
                      try {
                        espNames = JSON.parse(espListField.value);
                      } catch (e) {
                        console.error('Failed to parse espList value:', e);
                      }

                      for (const category of this.categories) {
                        for (const item of category.items) {
                          item.subscribed = espNames.includes(item.name);
                        }
                      }
                      this.initialSubscribedNames = [...espNames];
                    }

                    const emailVerifiedField = data.custom_field_values.find((f: any) => f.field_name === 'emailVerified');
                    this.isEmailVerified = emailVerifiedField?.value === 'true';

                    if (agreementField && agreementField.value == 'true') {
                      this.agreementConfirmed = true;
                      this.agreementChecked = true;
                      this.initialAgreement = true;
                    }
                  } else {
                    console.log('No custom field data found.');
                  }
                },
              });
            }
          },
        ]);
      } else {
        setTimeout(initPiano, 100);
      }
    };

    if (this.customerToken) {
      this.fetchSubscribedItems().then(() => {
        initPiano();
      });
    } else {
      this.$watch(
        () => this.customerToken,
        (token) => {
          if (token) {
            this.fetchSubscribedItems().then(() => {
              initPiano();
            });
          }
        }
      );
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
});
